/**
 * DiffTable — molecule (CES standard)
 * Visual diff of the proposed configuration against the current one, in
 * the client portal's DataTable style (gray header, divided rows).
 * `diff-delta--add`/`diff-delta--remove` stay as marker classes (E2E).
 */

import type { InstancePoolDiffEntry } from 'slotting-core';

const TH_CLASSES =
  'px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider';
const TD_CLASSES = 'px-4 py-2.5 text-sm text-gray-900 tabular-nums';

export function DiffTable({ diff }: { diff: InstancePoolDiffEntry[] }) {
  const changed = diff.filter((entry) => entry.delta !== 0);

  if (changed.length === 0) {
    return <p className="text-sm text-gray-400">No changes yet</p>;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
      <table className="w-full bg-white" data-testid="diff-table">
        <thead className="bg-gray-200 border-b border-gray-200">
          <tr>
            <th scope="col" className={TH_CLASSES}>
              Instance type
            </th>
            <th scope="col" className={TH_CLASSES}>
              Current
            </th>
            <th scope="col" className={TH_CLASSES}>
              Proposed
            </th>
            <th scope="col" className={TH_CLASSES}>
              Δ
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {changed.map((entry) => (
            <tr
              key={entry.instanceType}
              className="hover:bg-gray-50 transition-colors duration-150"
              data-testid={`diff-row-${entry.instanceType}`}
            >
              <td className={TD_CLASSES}>{entry.instanceType}</td>
              <td className={TD_CLASSES}>{entry.currentCount}</td>
              <td className={TD_CLASSES}>{entry.proposedCount}</td>
              <td
                className={`${TD_CLASSES} font-bold ${
                  entry.delta > 0
                    ? 'diff-delta--add text-green-600'
                    : 'diff-delta--remove text-red-600'
                }`}
              >
                {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
